import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ResetPasswordAction, ValidateResetTokenAction } from "../../store/action";
import AuthShell from "../ui/AuthShell";
import {
  fieldClass,
  fieldErrorClass,
  labelClass,
  primaryButtonClass,
  textLinkClass,
} from "../ui/formStyles";

/** Mirrors the API's minimum so the form rejects what the API would reject. */
const MIN_PASSWORD_LENGTH = 8;

/**
 * Landing page for the emailed reset link (/reset-password?token=...).
 *
 * Checks the link first and only then offers the form — asking someone to
 * choose a password and revealing afterwards that the link had expired wastes
 * the one thing they came here to do.
 */
const ResetPassword = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [phase, setPhase] = useState("checking");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setPhase("invalid");
      return;
    }

    let cancelled = false;

    const check = async () => {
      try {
        const data = await ValidateResetTokenAction(token);
        if (cancelled) return;
        setPhase(data?.valid ? "form" : "invalid");
      } catch (err) {
        if (cancelled) return;
        setPhase("invalid");
      }
    };

    check();

    // Validation is read-only, so unlike activation this is safe to run twice —
    // the flag only stops a late response from overwriting newer state.
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <AuthShell className="reset-content w-full max-w-md">
      <div className="text-center">
        <span
          className={`inline-flex size-12 items-center justify-center rounded-xl ${
            phase === "invalid" ? "bg-red-50 text-red-600" : "bg-brand-50 text-brand-600"
          }`}
        >
          <i
            className={`bx ${phase === "done" ? "bx-check" : "bx-lock-alt"} text-2xl`}
            aria-hidden="true"
          />
        </span>

        <h1 className="reset-title mt-4 text-fluid-xl font-bold tracking-tight text-ink-900">
          {phase === "checking" && t("ResetPassword.checking")}
          {phase === "form" && t("ResetPassword.formTitle")}
          {phase === "done" && t("ResetPassword.doneTitle")}
          {phase === "invalid" && t("ResetPassword.invalidTitle")}
        </h1>

        {phase === "invalid" && (
          <p className="mt-2 text-fluid-sm text-ink-500" role="alert">
            {error || t("ResetPassword.invalidBody")}
          </p>
        )}

        {phase === "done" && (
          <p className="mt-2 text-fluid-sm text-ink-500">
            {t("ResetPassword.doneBody")}
          </p>
        )}
      </div>

      {phase === "form" && (
        <ResetForm
          token={token}
          onDone={() => setPhase("done")}
          onDeadLink={(message) => {
            setError(message);
            setPhase("invalid");
          }}
        />
      )}

      {phase === "done" && (
        <Link to="/login" className={`${primaryButtonClass} mt-7`}>
          {t("ResetPassword.continueToLogin")}
        </Link>
      )}

      {phase === "invalid" && (
        <Link to="/forgot-password" className={`${primaryButtonClass} mt-7`}>
          {t("ResetPassword.requestNew")}
        </Link>
      )}

      <span className="mt-6 block text-center text-fluid-sm text-ink-500">
        {t("ResetPassword.backTo")}{" "}
        <Link to="/login" className={textLinkClass}>
          {t("ResetPassword.loginLink")}
        </Link>
      </span>
    </AuthShell>
  );
};

const ResetForm = ({ token, onDone, onDeadLink }) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationError, setValidationError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (password.length < MIN_PASSWORD_LENGTH) {
      setValidationError(
        t("ResetPassword.form.tooShort", { min: MIN_PASSWORD_LENGTH }),
      );
      return;
    }

    if (password !== confirmPassword) {
      setValidationError(t("ResetPassword.form.mismatch"));
      return;
    }

    setValidationError("");
    setSubmitting(true);

    try {
      await ResetPasswordAction(token, password);
      onDone();
    } catch (error) {
      const code = error.response?.data?.code;
      const message = error.response?.data?.message;

      // A dead token is not a form error — the whole page has to change, since
      // there is nothing useful left to submit here.
      if (code === "INVALID_TOKEN") {
        onDeadLink(message);
      } else {
        setValidationError(message || t("ResetPassword.form.genericError"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-8">
      <div className="form-item">
        <label htmlFor="reset-password" className={labelClass}>
          {t("ResetPassword.form.newPassword")}
        </label>
        <input
          type="password"
          id="reset-password"
          name="password"
          className={fieldClass}
          placeholder={t("ResetPassword.form.newPassword")}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
          required
        />
        <p className="mt-1.5 text-xs text-ink-400">
          {t("ResetPassword.form.hint", { min: MIN_PASSWORD_LENGTH })}
        </p>
      </div>

      <div className="form-item mt-4">
        <label htmlFor="reset-confirm-password" className={labelClass}>
          {t("ResetPassword.form.confirmNewPassword")}
        </label>
        <input
          type="password"
          id="reset-confirm-password"
          name="confirmPassword"
          className={validationError ? fieldErrorClass : fieldClass}
          placeholder={t("ResetPassword.form.confirmNewPassword")}
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          autoComplete="new-password"
          aria-invalid={validationError ? "true" : undefined}
          aria-describedby={validationError ? "reset-password-error" : undefined}
          required
        />
        {validationError && (
          <div
            id="reset-password-error"
            role="alert"
            className="error-message mt-1.5 flex items-center gap-1.5 text-xs font-medium text-red-600"
          >
            <i className="bi bi-exclamation-circle-fill" aria-hidden="true" />
            {validationError}
          </div>
        )}
      </div>

      <div className="form-item mt-7">
        <button type="submit" className={primaryButtonClass} disabled={submitting}>
          {submitting
            ? t("ResetPassword.form.submitting")
            : t("ResetPassword.form.submit")}
        </button>
      </div>
    </form>
  );
};

export default ResetPassword;
